import { useState, useEffect } from 'react';
import axios from 'axios';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Check, ShoppingCart, Loader2, Package, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BILLING_CYCLES = [
  { value: 'monthly',     label: 'Monthly',     months: 1,  days: 30   },
  { value: 'quarterly',   label: 'Quarterly',   months: 3,  days: 90   },
  { value: 'half_yearly', label: 'Half Yearly',  months: 6, days: 180  },
  { value: 'yearly',      label: 'Yearly',      months: 12, days: 365  },
  { value: 'two_years',   label: 'Two Years',   months: 24, days: 730  },
  { value: 'three_years', label: 'Three Years', months: 36, days: 1095 },
];

export default function OurProducts() {
  const { user } = useAuth();
  const [storefront, setStorefront] = useState(null);
  const [loading, setLoading]       = useState(true);

  // Billing cycle modal
  const [billingModal, setBillingModal]       = useState({ open: false, product: null });
  const [selectedCycle, setSelectedCycle]     = useState('monthly');
  const [orderSubmitting, setOrderSubmitting] = useState(false);
  const [orderConfirmed, setOrderConfirmed]   = useState(false);
  const [orderQty, setOrderQty]               = useState(1);

  useEffect(() => { fetchStorefront(); }, []);

  const fetchStorefront = async () => {
    try {
      const res = await axios.get(`${API}/client/partner-storefront`);
      setStorefront(res.data);
    } catch {
      toast.error('Failed to load products');
    } finally {
      setLoading(false);
    }
  };

  const openBillingModal = (product) => {
    // Set selected cycle to first enabled cycle for this product
    const enabledCycles = product.enabled_billing_cycles && product.enabled_billing_cycles.length > 0
      ? product.enabled_billing_cycles
      : BILLING_CYCLES.map(c => c.value);
    const firstCycle = BILLING_CYCLES.find(c => enabledCycles.includes(c.value))?.value || 'monthly';
    setSelectedCycle(firstCycle);
    setOrderConfirmed(false);
    setOrderQty(1);
    setBillingModal({ open: true, product });
  };

  const handlePlaceOrder = async () => {
    if (!billingModal.product || !selectedCycle) return;
    const product   = billingModal.product;
    const prices    = product.billing_prices_display || {};
    const cycleInfo = BILLING_CYCLES.find(c => c.value === selectedCycle) || BILLING_CYCLES[0];
    const unitPrice = prices[selectedCycle] || product.display_price || 0;
    const qty = (product.pricing_unit === 'per_user' || product.pricing_unit === 'per_account') ? orderQty : 1;

    setOrderSubmitting(true);
    try {
      await axios.post(`${API}/client/order-requests`, {
        product_id:       product.id,
        product_name:     product.name || '',
        product_category: product.category || 'Hosting',
        billing_cycle:    selectedCycle,
        unit_price:       unitPrice,
        quantity:         qty,
        validity_days:    cycleInfo.days,
        source:           'our_products',
      });
      setOrderConfirmed(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to place order. Please try again.');
    } finally {
      setOrderSubmitting(false);
    }
  };

  const showPricing = storefront?.settings?.show_pricing !== false;

  const getPriceForCycle = (product, cycle) => {
    if (!product) return null;
    const prices = product.billing_prices_display || {};
    return prices[cycle] || product.display_price || null;
  };

  return (
    <DashboardLayout role="client">
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Our Products</h1>
          <p className="text-slate-500 mt-1">
            {storefront?.settings?.company_name
              ? `Products available from ${storefront.settings.company_name}`
              : 'Browse and order products from your service provider'}
          </p>
        </div>

        {loading ? (
          <div className="flex justify-center py-20">
            <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
          </div>
        ) : !storefront?.products?.length ? (
          <div className="flex flex-col items-center justify-center py-20 text-center">
            <Package className="h-12 w-12 text-slate-300 mb-4" />
            <h3 className="text-lg font-medium text-slate-600">No products available</h3>
            <p className="text-slate-400 text-sm mt-1">Your service provider hasn&apos;t listed any products yet.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {storefront.products.map((product) => (
              <Card key={product.id} className="hover:shadow-lg transition-shadow" data-testid={`our-product-${product.id}`}>
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between">
                    <div>
                      <Badge variant="secondary" className="text-xs mb-2">{product.category}</Badge>
                      <CardTitle className="text-xl">{product.name}</CardTitle>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  {product.description && (
                    <p className="text-slate-500 text-sm">{product.description}</p>
                  )}

                  {showPricing && product.display_price ? (
                    <div className="bg-slate-50 rounded-lg px-4 py-3">
                      {(() => {
                        const enabledCycles = product.enabled_billing_cycles && product.enabled_billing_cycles.length > 0
                          ? product.enabled_billing_cycles
                          : ['monthly'];
                        const isMontlyEnabled = enabledCycles.includes('monthly');
                        const prices = product.billing_prices_display || {};
                        const firstCycle = BILLING_CYCLES.find(c => enabledCycles.includes(c.value));
                        const showPrice = isMontlyEnabled ? product.display_price : (prices[firstCycle?.value] || product.display_price);
                        const showLabel = isMontlyEnabled ? '/month' : `/${firstCycle?.label || 'cycle'}`;
                        return (
                          <>
                            <div className="flex items-baseline gap-1">
                              <span className="text-2xl font-bold text-blue-600">
                                ₹{showPrice?.toLocaleString()}
                              </span>
                              <span className="text-slate-400 text-sm">{showLabel}</span>
                              {(product.pricing_unit === 'per_user' || product.pricing_unit === 'per_account') && (
                                <span className="text-xs text-purple-600 bg-purple-50 border border-purple-200 rounded px-1.5 py-0.5 ml-1">
                                  {product.pricing_unit === 'per_user' ? 'per user' : 'per account'}
                                </span>
                              )}
                            </div>
                            <p className="text-xs text-slate-400 mt-0.5">Starting price</p>
                          </>
                        );
                      })()}
                    </div>
                  ) : null}

                  {product.features && product.features.length > 0 && (
                    <ul className="space-y-2">
                      {product.features.slice(0, 4).map((feature, idx) => (
                        <li key={idx} className="flex items-start gap-2">
                          <Check className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                          <span className="text-sm text-slate-600">{feature}</span>
                        </li>
                      ))}
                    </ul>
                  )}

                  <Button
                    className="w-full"
                    onClick={() => openBillingModal(product)}
                    data-testid={`order-our-product-${product.id}`}
                  >
                    <ShoppingCart className="h-4 w-4 mr-2" />
                    Order Now
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* ── Billing Cycle Selector Modal ── */}
      <Dialog
        open={billingModal.open}
        onOpenChange={open => { if (!orderSubmitting) { setBillingModal(m => ({ ...m, open })); if (!open) setOrderConfirmed(false); }}}
      >
        <DialogContent className="sm:max-w-md" data-testid="our-products-billing-modal">
          {!orderConfirmed ? (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <ShoppingCart className="h-5 w-5 text-blue-600" />
                  Select Billing Cycle
                </DialogTitle>
                <p className="text-sm text-slate-500 mt-1">
                  Plan: <strong>{billingModal.product?.name}</strong>
                </p>
              </DialogHeader>

              <div className="space-y-2 py-2">
                {/* Quantity for per-user/per-account products */}
                {(billingModal.product?.pricing_unit === 'per_user' || billingModal.product?.pricing_unit === 'per_account') && (
                  <div className="bg-purple-50 border border-purple-200 rounded-lg px-3 py-3 flex items-center gap-3">
                    <div className="flex-1">
                      <p className="text-sm font-medium text-purple-800">
                        Number of {billingModal.product?.pricing_unit === 'per_user' ? 'Users' : 'Accounts'}
                      </p>
                      {billingModal.product?.max_quantity && (
                        <p className="text-xs text-purple-600 mt-0.5">Max {billingModal.product.max_quantity}</p>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <button onClick={() => setOrderQty(q => Math.max(1, q - 1))} className="w-7 h-7 rounded-full bg-white border border-purple-300 text-purple-700 font-bold text-lg flex items-center justify-center hover:bg-purple-100">−</button>
                      <input
                        type="number"
                        min="1"
                        max={billingModal.product?.max_quantity || 9999}
                        value={orderQty}
                        onChange={e => setOrderQty(Math.max(1, parseInt(e.target.value) || 1))}
                        className="w-14 text-center border border-purple-300 rounded-md px-1 py-1 text-sm font-semibold focus:ring-purple-500 focus:border-purple-500"
                        data-testid="order-qty-input"
                      />
                      <button onClick={() => setOrderQty(q => billingModal.product?.max_quantity ? Math.min(billingModal.product.max_quantity, q + 1) : q + 1)} className="w-7 h-7 rounded-full bg-white border border-purple-300 text-purple-700 font-bold text-lg flex items-center justify-center hover:bg-purple-100">+</button>
                    </div>
                  </div>
                )}
                {BILLING_CYCLES
                  .filter(cycle => {
                    const enabled = billingModal.product?.enabled_billing_cycles;
                    return !enabled || enabled.length === 0 || enabled.includes(cycle.value);
                  })
                  .map((cycle) => {
                  const price = getPriceForCycle(billingModal.product, cycle.value);
                  const isSelected = selectedCycle === cycle.value;
                  return (
                    <button
                      key={cycle.value}
                      onClick={() => setSelectedCycle(cycle.value)}
                      data-testid={`our-products-cycle-${cycle.value}`}
                      className={`w-full flex items-center justify-between px-4 py-3 rounded-lg border-2 transition-all text-left
                        ${isSelected
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-slate-200 bg-white hover:border-slate-300'
                        }`}
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center
                          ${isSelected ? 'border-blue-500' : 'border-slate-300'}`}
                        >
                          {isSelected && <div className="w-2 h-2 rounded-full bg-blue-500" />}
                        </div>
                        <div>
                          <p className={`font-medium text-sm ${isSelected ? 'text-blue-700' : 'text-slate-800'}`}>
                            {cycle.label}
                          </p>
                          <p className="text-xs text-slate-400">{cycle.months} month{cycle.months > 1 ? 's' : ''}</p>
                        </div>
                      </div>
                      {showPricing && price ? (
                        <div className="text-right">
                          <p className={`font-semibold text-sm ${isSelected ? 'text-blue-700' : 'text-slate-700'}`}>
                            ₹{price.toLocaleString()}
                            {(billingModal.product?.pricing_unit === 'per_user' || billingModal.product?.pricing_unit === 'per_account') && (
                              <span className="text-xs font-normal text-slate-400 ml-0.5">
                                ×{orderQty} = ₹{(price * orderQty).toLocaleString()}
                              </span>
                            )}
                          </p>
                          <p className="text-xs text-slate-400">
                            ₹{Math.round(price / cycle.months).toLocaleString()}/mo
                          </p>
                        </div>
                      ) : null}
                    </button>
                  );
                })}
              </div>

              <div className="bg-blue-50 rounded-lg px-3 py-2 flex items-start gap-2">
                <AlertCircle className="h-4 w-4 text-blue-500 mt-0.5 flex-shrink-0" />
                <p className="text-xs text-blue-700">
                  Your order will be sent to your service provider for review. You&apos;ll receive a confirmation email shortly.
                </p>
              </div>

              <div className="flex gap-3 pt-1">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => setBillingModal(m => ({ ...m, open: false }))}
                  disabled={orderSubmitting}
                  data-testid="our-products-cancel"
                >
                  Cancel
                </Button>
                <Button
                  className="flex-1"
                  onClick={handlePlaceOrder}
                  disabled={orderSubmitting || !selectedCycle}
                  data-testid="our-products-place-order"
                >
                  {orderSubmitting
                    ? <><Loader2 className="h-4 w-4 animate-spin mr-2" />Placing…</>
                    : 'Place Order'}
                </Button>
              </div>
            </>
          ) : (
            <div className="text-center py-6 space-y-4" data-testid="our-products-order-confirmed">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-50 mx-auto">
                <Check className="h-8 w-8 text-green-600" />
              </div>
              <h3 className="text-xl font-semibold text-slate-800">Order Placed!</h3>
              <p className="text-slate-600 text-sm leading-relaxed">
                Your order for <strong>{billingModal.product?.name}</strong> (
                {BILLING_CYCLES.find(c => c.value === selectedCycle)?.label}) has been submitted.
                Your service provider will review it and respond shortly.
              </p>
              <div className="flex gap-3 pt-1">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => { setBillingModal({ open: false, product: null }); setOrderConfirmed(false); }}
                  data-testid="our-products-confirmed-close"
                >
                  Close
                </Button>
                <Button
                  className="flex-1"
                  onClick={() => { setBillingModal({ open: false, product: null }); setOrderConfirmed(false); fetchStorefront(); }}
                  data-testid="our-products-confirmed-browse"
                >
                  Browse More
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
